import React, { useState } from 'react';
import { X, FileText, MessageCircle, ThumbsUp, Eye } from 'lucide-react';

const getSettingsFromForm = (formData) => {
    return {
        featureTypes: [
            formData.required && 'required',
            formData.nice_to_have && 'nice_to_have',
            formData.not_needed && 'not_needed',
            formData.need_more_info && 'need_more_info'
        ].filter(Boolean),
        includeComments: formData.includeComments,
        includeReactions: formData.includeReactions,
        showUserDetails: formData.showUserDetails,
        includeAISummary: formData.includeAISummary,
        includeUnresponded: formData.includeUnresponded,
        paperSize: formData.paperSize
    };
};

const ReportSettingsModal = ({ onClose, onGenerate }) => {
    const [settings, setSettings] = useState({
        includeComments: true,
        includeReactions: true,
        showUserDetails: true,
        includeAISummary: true,
        includeUnresponded: false,
        paperSize: 'A4',
        featureTypes: {
            required: true,
            nice_to_have: true,
            not_needed: true,
            dont_know: true
        }
    });

    const prepareSettingsForReport = (settings) => {
        const featureTypes = Object.entries(settings.featureTypes)
            .filter(([, isSelected]) => isSelected)
            .map(([type]) => {
                if (type === 'dont_know') return 'need_more_info';
                return type;
            });

        return {
            featureTypes,
            includeComments: settings.includeComments,
            includeReactions: settings.includeReactions,
            showUserDetails: settings.showUserDetails,
            includeAISummary: settings.includeAISummary,
            includeUnresponded: settings.includeUnresponded,
            paperSize: settings.paperSize,
            // Add generation timestamp for report metadata
            generatedAt: new Date().toISOString()
        };
    };
    
    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFeatureTypeChange = (type) => {
        setSettings(prev => ({
            ...prev,
            featureTypes: {
                ...prev.featureTypes,
                [type]: !prev.featureTypes[type]
            }
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Report Settings
                    </h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    {/* Paper Size Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Paper Size
                        </label>
                        <select
                            value={settings.paperSize}
                            onChange={handleChange('paperSize')}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500"
                        >
                            <option value="A4">A4</option>
                            <option value="Letter">Letter</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Include Feature Types
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.featureTypes.required}
                                    onChange={() => handleFeatureTypeChange('required')}
                                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">Required</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.featureTypes.nice_to_have}
                                    onChange={() => handleFeatureTypeChange('nice_to_have')}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">Nice to Have</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.featureTypes.not_needed}
                                    onChange={() => handleFeatureTypeChange('not_needed')}
                                    className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">Not Needed</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.featureTypes.dont_know}
                                    onChange={() => handleFeatureTypeChange('dont_know')}
                                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">Need More Info</span>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-2 pt-4 border-t">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={settings.includeComments}
                                onChange={handleChange('includeComments')}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700 flex items-center gap-1">
                                <MessageCircle className="w-4 h-4" />
                                Include Comments
                            </span>
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={settings.includeReactions}
                                onChange={handleChange('includeReactions')}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700 flex items-center gap-1">
                                <ThumbsUp className="w-4 h-4" />
                                Include Reactions
                            </span>
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={settings.includeUnresponded}
                                onChange={handleChange('includeUnresponded')}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700 flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                Include Unresponded Features
                            </span>
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={settings.showUserDetails}
                                onChange={handleChange('showUserDetails')}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700">
                                Show User Details
                            </span>
                        </label>

                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={settings.includeAISummary}
                                onChange={handleChange('includeAISummary')}
                                className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700">
                                Include AI Executive Summary
                            </span>
                        </label>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onGenerate(prepareSettingsForReport(settings))}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                        Generate Report
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportSettingsModal;